import { loadOrganizationScopeSettings } from '@features/lifecycles/organization-scopes.hooks';
import { ScopeContextSync } from '@features/scope/ScopeContextSync';
import { loadSavedScopes } from '@features/lifecycles/saved-scopes.hooks';
import { NODON_PROJECTS_LIST_PATH, SHARED_PROJECT } from '@shared/constants';
import { isPublicProjectPathname } from '@shared/constants/router.constants';
import { pick } from '@shared/helpers/object.helpers';
import { projectToProjectInfo } from '@shared/helpers/project.helpers';
import { safeMartinezUnion } from '@shared/helpers/geometry/martinez-polygon-clipping-ops.helpers';
import {
  buildGenerationProjectPath,
  buildSharedProjectPath,
  buildStrictProjectPath,
} from '@shared/helpers/router.helpers';
import { IProjectInfo } from '@shared/models/project.model';
import { User } from '@shared/models/user.model';
import { GlobalLoader } from '@ui/GlobalLoader';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { NODON_GENERATION_PROJECT_MATCH_PATH } from '@shared/constants/router.constants';
import {
  SELECTED_ELEMENT_SEARCH_PARAM,
  URL_CATEGORY_NONE,
} from '@client/models/router.model';

import { useObjectMemoDeep } from './hooks/use-object.hooks';
import {
  getFallbackOrg,
  ProjectParamsObject,
  RawProjectParamsObject,
  useRawProjectParams,
  useUrlToUiStoreSync,
  selectionParamsFromRoute,
  validateAndCompleteProjectParams,
  validateGenerationProjectParams,
  validateSharedProjectParams,
} from './hooks/use-router.hooks';
import { useUser } from './hooks/use-user.hooks';
import { usePrevious } from './hooks/use-utils.hooks';
import { useCommentsStore } from './store/comment/comment.store';
import { useSelectedOrganizationName } from './store/organization';
import { useProductStoreState } from './store/product';
import {
  useProjectState,
  useSortedByLastUpdatedProjects,
} from './store/project';
import { useBuildingLocation } from '@features/design/hooks/design.hooks';
import { useRecipesStore } from './store/recipe';
import { useUserStore } from './store/user/user.store';
import { isAuthDisabled } from './providers/AuthDisabledProvider';
import { usePvgisState } from './store/pvgis';
import {
  useDataTableIsLoading,
  useFetchDataTables,
} from '@features/data-table/store';
import { useFolderState } from './store/folder';
import {
  clearNodonAppReady,
  setNodonAppReady,
  type NodonAppReadyView,
} from './helpers/app-readiness.helpers';
import { getErrorRecoveryMode } from './helpers/error-boundary.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  buildNotFoundHref,
  decideResourceErrorNavigation,
  getResourceLoadRetryAction,
  nextResourceRetryBudget,
  resourceLoadFailureIdentity,
  type ResourceLoadRetryAction,
  type ResourceLoadRetryRoute,
  type ResourceRetryBudget,
} from './helpers/resource-load.helpers';
import { useInterval } from 'usehooks-ts';

const anonymousUser: User = {
  id: '__anonymous__',
  name: 'Guest',
  nickname: 'Guest',
  picture: '',
  organizations: [],
} as const;

export const DataLoaderProxy: FC<{
  children: ReactNode;
  isShared?: boolean;
}> = ({ children, isShared: isSharedProp }) => {
  const { pathname } = useLocation();
  const rawParams = useRawProjectParams();
  const maybeUser = useUser();
  const isPublicRoute = isSharedProp || isPublicProjectPathname(pathname);
  const user = isPublicRoute ? anonymousUser : maybeUser;
  return useMemo(
    () =>
      !user ? (
        <GlobalLoader.Register text="Loading Nodon" />
      ) : (
        <DataLoaderProxyComponent rawParams={rawParams} user={user}>
          {children}
        </DataLoaderProxyComponent>
      ),
    [children, user, rawParams],
  );
};

const DataLoaderProxyComponent: FC<{
  children: ReactNode;
  rawParams: RawProjectParamsObject;
  user: User;
}> = React.memo(({ children, rawParams, user }) => {
  const navigate = useNavigate();
  const currentUIStateOrg = useSelectedOrganizationName();
  const currentOrg = getCurrentOrg(rawParams, user);
  const urlOrg = getUrlOrg(rawParams);
  const { fetchRecipes, fetching: isRecipesLoading } = useRecipesStore();
  const { fetchUsers, isLoading: isUsersLoading } = useUserStore();
  const { fetchComments } = useCommentsStore();
  const { fetchProducts, isLoading: isProductsLoading } = useProductStoreState(
    'fetchProducts',
    'isLoading',
  );
  const fetchDataTables = useFetchDataTables();
  const isDataTablesLoading = useDataTableIsLoading();
  const { fetchFolders, foldersFetching } = useFolderState(
    'fetchFolders',
    'foldersFetching',
  );
  const {
    project,
    fetchProject,
    fetchPublicProject,
    fetchGenerationProject,
    fetchProjects,
    fetchPublicProjects,
    projectsFetching,
    publicProjectsFetching,
    projectError,
    isLoading: isProjectLoading,
  } = useProjectState(
    'project',
    'fetchProject',
    'fetchPublicProject',
    'fetchGenerationProject',
    'fetchProjects',
    'fetchPublicProjects',
    'projectsFetching',
    'publicProjectsFetching',
    'projectError',
    'isLoading',
  );
  const resourceErrorRecoveryMode = getErrorRecoveryMode();

  // Get building coordinates from the current project
  const buildingLocation = useBuildingLocation();
  const { fetchPvgisData } = usePvgisState('fetchPvgisData');

  const prevUrlOrg = usePrevious(urlOrg);
  // QUICK FIX: when org changes, there's chicken & the egg issues. To avoid this, currently we just reload the whole page.
  // TODO: Straighten out auth/user/organization loading so we don't have to do this.
  // NOTE: this only happens on test/prod, so removing this on dev will look like everything is fine but it's not.
  if (
    prevUrlOrg !== undefined &&
    urlOrg !== undefined &&
    prevUrlOrg !== urlOrg
  ) {
    window.location.reload();
  }

  const { pathname, search } = useLocation();

  // always fetch projects when the organization changes.
  useEffect(() => {
    if (rawParams.type !== 'project' || !currentOrg) return;
    fetchProjects(currentOrg);
    fetchPublicProjects();
  }, [rawParams.type, currentOrg, fetchProjects, fetchPublicProjects]);
  const fallbackProjects = useSortedByLastUpdatedProjects(currentOrg);
  const projectsForValidation = useMemo(() => {
    if (
      rawParams.type !== 'project' ||
      String(project.id) !== rawParams.data.projectId
    ) {
      return fallbackProjects;
    }

    // The loaded project is the source of truth for its versions. The lighter
    // project-list row can temporarily have stale `versionIds` after a version
    // is duplicated, which otherwise makes URL validation bounce a valid
    // version selection back to the previous active version.
    const loadedProjectInfo = projectToProjectInfo(project, safeMartinezUnion);
    const projects = fallbackProjects.projects.some(
      (candidate) => String(candidate.id) === String(project.id),
    )
      ? fallbackProjects.projects.map((candidate) =>
          String(candidate.id) === String(project.id)
            ? loadedProjectInfo
            : candidate,
        )
      : [...fallbackProjects.projects, loadedProjectInfo];

    return { ...fallbackProjects, projects };
  }, [fallbackProjects, project, rawParams]);

  const params = useObjectMemoDeep(
    validateParams(
      rawParams,
      user,
      projectsForValidation,
      project.active_version_id,
      pathname,
    ),
  );
  const isPublicProjectRoute =
    isPublicProjectPathname(pathname) ||
    params.type === 'shared' ||
    params.type === 'generation';
  useUrlToUiStoreSync(params);
  const pathFromParams = getPathFromParams(params);
  useEffect(() => {
    if (pathFromParams && pathFromParams !== pathname) {
      // Canonicalize the URL (e.g. upgrade a legacy `/mc/…/e/<id>` link) while
      // preserving current query params and carrying element selection over to
      // `?selected=` (DEV-3454).
      navigate(rerouteTargetWithSearch(pathFromParams, params), {
        replace: true,
      });
    }
  }, [navigate, pathname, pathFromParams, params]);

  // props that will trigger reloads
  const reloadProps = useObjectMemoDeep(getReloadProps(params));
  useEffect(() => {
    switch (reloadProps.type) {
      case 'project': {
        const numId = parseInt(reloadProps.data.projectId, 10);
        fetchProject(numId, reloadProps.data.organization);
        fetchComments(numId);
        break;
      }
      case 'project-list':
        break;
      case 'shared': {
        fetchPublicProject(reloadProps.data.sharingKey);
        break;
      }
      case 'generation': {
        fetchGenerationProject(reloadProps.data.generationKey);
        break;
      }
    }
  }, [
    reloadProps,
    fetchComments,
    fetchProject,
    fetchPublicProject,
    fetchGenerationProject,
  ]);
  const isProjectMismatch =
    params.type === 'project' && project.id !== Number(params.data.projectId);

  useEffect(() => {
    if (params.type !== 'project') return;
    if (!isProjectMismatch) return;
    const urlNumID = Number(params.data.projectId);
    // if the project in the store and the URL doesn't match for more than 5 seconds, fetch the project again
    // this most likely happened because some part of the code has manually updated the current project in the store to a different project.
    // the store project must only be updated if it's the same project as the one in the URL.
    // future TODO, we should not have a single "project" record in the store.
    const timeout = setTimeout(() => {
      fetchProject(urlNumID);
    }, 5000);
    return () => {
      // if the effect is re-run, abandon the project fetch (project mismatch was probably because of late react-router state update)
      clearTimeout(timeout);
    };
  }, [isProjectMismatch, params, fetchProject]);

  useEffect(() => {
    if (isPublicProjectRoute) return;
    // these are dependent on organization
    if (currentUIStateOrg && currentUIStateOrg !== SHARED_PROJECT) {
      void loadSavedScopes(currentUIStateOrg);
      void loadOrganizationScopeSettings(currentUIStateOrg);
      fetchUsers(currentUIStateOrg);
      fetchRecipes(currentUIStateOrg);
      fetchProducts(currentUIStateOrg);
      fetchDataTables(currentUIStateOrg);
      fetchFolders(currentUIStateOrg).catch(console.error);
    }
  }, [
    isPublicProjectRoute,
    currentUIStateOrg,
    fetchUsers,
    fetchRecipes,
    fetchProducts,
    fetchDataTables,
    fetchFolders,
  ]);

  // Fetch PVGIS weather data when building coordinates change.
  // E2e (auth disabled) skips this: CI cannot reach JRC, and a late response
  // remounts resolved versions and discards in-flight property edits.
  useEffect(() => {
    if (isAuthDisabled()) return;
    const { lat, lng } = buildingLocation;
    if (project.id === 0) return; // skip for default empty project
    if (lat === 0 || lng === 0) return; // skip if no coordinates

    fetchPvgisData({ lat, lng }).catch((error) => {
      console.error('PVGIS weather data fetch failed:', error);
    });
  }, [project.id, buildingLocation, fetchPvgisData]);

  const matchingStoreProject =
    params.type === 'project' && project.id === Number(params.data.projectId);

  let activeLoadError: Error | undefined;
  if (params.type === 'error' && params.data.action === 'redirect') {
    activeLoadError = params.data.error;
  } else if (
    projectError &&
    isLoadableProjectRoute(params.type) &&
    !matchingStoreProject
  ) {
    activeLoadError = projectError;
  }

  const loadErrorKey = activeLoadError?.message ?? '';
  const retryRoute = toResourceLoadRetryRoute(params, rawParams, currentOrg);
  const failureIdentity = loadErrorKey
    ? resourceLoadFailureIdentity(retryRoute, loadErrorKey)
    : '';
  // A refetch clears the error before the request settles. That gap is not a
  // successful load, so it must not zero the retry budget.
  const isRetryInFlight =
    isProjectLoading || projectsFetching || publicProjectsFetching;
  const [retryBudget, setRetryBudget] = useState<ResourceRetryBudget>({
    count: 0,
    trackedFailure: '',
  });
  const nextBudget = nextResourceRetryBudget(retryBudget, {
    failureIdentity,
    isRetryInFlight,
  });
  if (nextBudget !== retryBudget) setRetryBudget(nextBudget);

  const resourceRetryAction = getResourceLoadRetryAction(retryRoute);

  const resourceLoadNavigation = activeLoadError
    ? decideResourceErrorNavigation({
        recoveryMode: resourceErrorRecoveryMode,
        error: activeLoadError,
        hasUsableCachedResource: false,
        productionRetryCount: nextBudget.count,
      })
    : undefined;

  const shouldRetryFailedResourceLoad = resourceLoadNavigation === 'wait';

  // A down API keeps retrying. A 404 stops after the retry cap and goes to `/not-found`.
  useInterval(
    () => {
      setRetryBudget((current) => ({ ...current, count: current.count + 1 }));
      applyResourceLoadRetry(resourceRetryAction, {
        fetchProjects,
        fetchProject,
        fetchPublicProject,
        fetchGenerationProject,
      });
    },
    shouldRetryFailedResourceLoad ? 3000 : null,
  );

  const loadingText = matchPath(NODON_PROJECTS_LIST_PATH, pathname)
    ? 'Loading Nodon'
    : 'Loading project';

  if (params.type === 'error') {
    switch (params.data.action) {
      case 'redirect': {
        if (resourceLoadNavigation === 'wait') {
          return <GlobalLoader.Register text={loadingText} />;
        }
        console.error(
          '[DataLoaderProxy] Redirecting to not-found:',
          params.data.error.message,
        );
        return (
          <Navigate
            to={buildNotFoundHref(pathname, search, params.data.error)}
            replace
          />
        );
      }
      case 'wait': {
        return <GlobalLoader.Register text={loadingText} />;
      }
    }
  }
  if (
    projectError &&
    params.type !== 'project-list' &&
    !isProjectLoading &&
    !matchingStoreProject
  ) {
    if (resourceLoadNavigation === 'wait') {
      return <GlobalLoader.Register text={loadingText} />;
    }
    console.error(
      '[DataLoaderProxy] Project error, redirecting to not-found:',
      projectError.message,
    );
    return (
      <Navigate
        to={buildNotFoundHref(pathname, search, projectError)}
        replace
      />
    );
  }

  // if any of the data is still loading, show a loading screen
  // On project list we don't block on projects/public projects fetch so navigation from project feels instant
  // Note: Weather data (PVGIS) loads in background and doesn't block UI
  const isProjectList = params.type === 'project-list';
  const blockOnProjectsFetch =
    !isProjectList &&
    !isPublicProjectRoute &&
    (projectsFetching || publicProjectsFetching);

  // Loading org data is blocking for authenticated project pages only
  const blockOnOrgData =
    !isPublicProjectRoute &&
    (isProductsLoading ||
      isRecipesLoading ||
      isUsersLoading ||
      isDataTablesLoading ||
      foldersFetching);

  const blockOnPublicProjectLoad =
    isPublicProjectRoute &&
    params.type !== 'project-list' &&
    isProjectLoading &&
    project.id === 0;

  if (blockOnProjectsFetch || blockOnOrgData || blockOnPublicProjectLoad) {
    return <GlobalLoader.Register text={loadingText} />;
  }

  if (isProjectMismatch) {
    // This shouldn't happen but because project updates happen instantly, there's sometimes a mismatch between url project ID and store project ID
    // In these cases, we can just wait for the URL to actually update. It should happen in the next render pass.
    return <GlobalLoader.Register text={loadingText} />;
  }

  return (
    <ScopeContextSync overview={isProjectList}>
      <NodonAppReadySignal view={appReadyViewFromParams(params)}>
        {children}
      </NodonAppReadySignal>
    </ScopeContextSync>
  );
});

/**
 * Sets {@link setNodonAppReady} while mounted so Playwright can wait on a
 * stable signal instead of racing URL changes against still-loading UI.
 */
const NodonAppReadySignal: FC<{
  view: NodonAppReadyView;
  children: ReactNode;
}> = ({ view, children }) => {
  useEffect(() => {
    setNodonAppReady(view);
    return () => clearNodonAppReady();
  }, [view]);
  return children;
};

/** Maps validated route params to the readiness attribute value. */
const appReadyViewFromParams = (
  params: ProjectParamsObject,
): NodonAppReadyView => {
  switch (params.type) {
    case 'project':
      return 'project';
    case 'project-list':
      return 'project-list';
    case 'shared':
    case 'generation':
      return 'shared';
    case 'error':
      return 'project-list';
  }
};

/**
 * Get the organization from the raw params. No fallback
 * @param rawParams - The raw params object.
 * @returns The organization.
 */
const getUrlOrg = (rawParams: RawProjectParamsObject) => {
  switch (rawParams.type) {
    case 'project':
      return rawParams.data.organization;
    case 'shared':
    case 'generation':
      return SHARED_PROJECT;
  }
};

/**
 * Get the current organization from the raw params. Uses User object for fallback for non-shared projects.
 * @param rawParams - The raw params object.
 * @param user - The user object.
 * @returns The current organization.
 */
const getCurrentOrg = (
  rawParams: RawProjectParamsObject,
  user: User,
): string | undefined => {
  const urlOrg = getUrlOrg(rawParams);

  const fallback = getFallbackOrg(user.organizations.map((org) => org.name));
  return urlOrg ?? fallback;
};

const getPathFromParams = (params: ProjectParamsObject) => {
  switch (params.type) {
    case 'project':
      return buildStrictProjectPath(
        pick(params.data, 'organization', 'projectId', 'versionId', 'page'),
      );
    case 'project-list':
      return generatePath(NODON_PROJECTS_LIST_PATH, params.data);
    case 'shared':
      return buildSharedProjectPath(
        pick(params.data, 'sharingKey', 'versionId', 'page'),
      );
    case 'generation':
      return buildGenerationProjectPath(
        pick(params.data, 'generationKey', 'versionId', 'page'),
      );
    case 'error':
      return undefined;
  }
};

/** Selected element id from validated params, or `undefined` for none. */
const getElementIdFromParams = (
  params: ProjectParamsObject,
): string | undefined => {
  switch (params.type) {
    case 'project':
    case 'shared':
    case 'generation': {
      const { elementId } = params.data;
      return elementId && elementId !== URL_CATEGORY_NONE
        ? elementId
        : undefined;
    }
    default:
      return undefined;
  }
};

/**
 * Appends the current query string to a canonical pathname, carrying element
 * selection over to `?selected=`. Used when rerouting a legacy path URL so a
 * bookmarked `/e/<id>` link keeps its selection after canonicalization.
 */
const rerouteTargetWithSearch = (
  pathname: string,
  params: ProjectParamsObject,
): string => {
  const search = new URLSearchParams(window.location.search);
  const elementId = getElementIdFromParams(params);
  if (elementId) {
    search.set(SELECTED_ELEMENT_SEARCH_PARAM, elementId);
  }
  const qs = search.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

const getReloadProps = (params: ProjectParamsObject) => {
  switch (params.type) {
    case 'project':
      return {
        type: 'project',
        data: pick(params.data, 'organization', 'projectId'),
      } as const;
    case 'project-list':
      return { type: 'project-list' } as const;
    case 'shared':
      return {
        type: 'shared',
        data: pick(params.data, 'sharingKey'),
      } as const;
    case 'generation':
      return {
        type: 'generation',
        data: pick(params.data, 'generationKey'),
      } as const;
    case 'error':
      return params;
  }
};

const isLoadableProjectRoute = (
  type: ProjectParamsObject['type'],
): type is 'project' | 'shared' | 'generation' =>
  type === 'project' || type === 'shared' || type === 'generation';

/** Map validated params to the refetch target for a failed resource load. */
const toResourceLoadRetryRoute = (
  params: ProjectParamsObject,
  rawParams: RawProjectParamsObject,
  currentOrg: string | undefined,
): ResourceLoadRetryRoute => {
  switch (params.type) {
    case 'project':
      return {
        type: 'project',
        organization: params.data.organization,
        projectId: params.data.projectId,
      };
    case 'project-list':
      return { type: 'project-list' };
    case 'shared':
      return { type: 'shared', sharingKey: params.data.sharingKey };
    case 'generation':
      return { type: 'generation', generationKey: params.data.generationKey };
    case 'error':
      return {
        type: 'error',
        raw: rawParamsToErrorRetryRaw(rawParams, currentOrg),
      };
  }
};

const rawParamsToErrorRetryRaw = (
  rawParams: RawProjectParamsObject,
  currentOrg: string | undefined,
): Extract<ResourceLoadRetryRoute, { type: 'error' }>['raw'] => {
  switch (rawParams.type) {
    case 'project':
      return { type: 'project', organization: currentOrg };
    case 'shared':
      return { type: 'shared', sharingKey: rawParams.data.sharingKey };
    case 'generation':
      return {
        type: 'generation',
        generationKey: rawParams.data.generationKey,
      };
  }
};

type ResourceLoadRetryFetchers = {
  fetchProjects: (org?: string) => Promise<unknown>;
  fetchProject: (id: number, org?: string) => Promise<unknown>;
  fetchPublicProject: (sharingKey: string) => Promise<unknown>;
  fetchGenerationProject: (generationKey: string) => Promise<unknown>;
};

/** Run the refetch that can recover the current load error. */
const applyResourceLoadRetry = (
  action: ResourceLoadRetryAction,
  fetchers: ResourceLoadRetryFetchers,
): void => {
  switch (action.type) {
    case 'none':
      return;
    case 'project':
      void fetchers.fetchProjects(action.organization).catch(() => undefined);
      void fetchers
        .fetchProject(action.projectId, action.organization)
        .catch(() => undefined);
      return;
    case 'projects':
      void fetchers.fetchProjects(action.organization).catch(() => undefined);
      return;
    case 'shared':
      void fetchers
        .fetchPublicProject(action.sharingKey)
        .catch(() => undefined);
      return;
    case 'generation':
      void fetchers
        .fetchGenerationProject(action.generationKey)
        .catch(() => undefined);
      return;
    default:
      assertUnreachable(action);
  }
};

const validateParams = (
  parsed: RawProjectParamsObject,
  user: User,
  projectsData: {
    isFetched: boolean;
    projects: IProjectInfo[];
    error?: Error | null;
  },
  fallbackVersionId?: string,
  pathname?: string,
): ProjectParamsObject => {
  if (
    pathname &&
    isPublicProjectPathname(pathname) &&
    parsed.type === 'project'
  ) {
    const matched = matchPath(
      NODON_GENERATION_PROJECT_MATCH_PATH,
      pathname,
    )?.params;
    if (matched?.generationKey && matched.versionId) {
      const result = validateGenerationProjectParams(
        {
          generationKey: matched.generationKey,
          versionId: matched.versionId,
          ...selectionParamsFromRoute(matched, window.location.search),
        },
        fallbackVersionId,
      );
      return 'error' in result
        ? { type: 'error', data: result }
        : { type: 'generation', data: result };
    }
  }

  switch (parsed.type) {
    case 'project': {
      const result = validateAndCompleteProjectParams(
        parsed.data,
        user.organizations.map((org) => org.name),
        projectsData,
      );

      switch (result.type) {
        case 'error':
          return { type: 'error', data: result };
        case 'project':
          return { type: 'project', data: result };
        case 'project-list':
          return { type: 'project-list', data: result };
      }
      break;
    }
    case 'shared': {
      const result = validateSharedProjectParams(
        parsed.data,
        fallbackVersionId,
      );
      return 'error' in result
        ? { type: 'error', data: result }
        : { type: 'shared', data: result };
    }
    case 'generation': {
      const result = validateGenerationProjectParams(
        parsed.data,
        fallbackVersionId,
      );
      return 'error' in result
        ? { type: 'error', data: result }
        : { type: 'generation', data: result };
    }
  }
};
